import React, { useState, useEffect } from 'react';
import { userService } from '../services/api';
import { useTranslation } from 'react-i18next';
import { desDeUTC } from '../utils/dates';
import toast from 'react-hot-toast';

function Users() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [filtre, setFiltre] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const [allUsers, pending] = await Promise.all([
        userService.getAllUsers(),
        userService.getPendingUsers()
      ]);
      setUsers(allUsers.data);
      setPendingUsers(pending.data);
    } catch (error) {
      toast.error(t('users.errorFetchingUsers'));
    } finally {
      setLoading(false);
    }
  };

  const approveUser = async (userId) => {
    try {
      await userService.approveUser(userId);
      toast.success(t('users.userApprovedSuccess'));
      fetchUsers(); // Refresh the lists
    } catch (error) {
      toast.error(t('users.errorApprovingUser'));
    }
  };

  const deleteUser = async (userId, username) => {
    if (!window.confirm(t('users.confirmDeleteUser', { username }))) {
      return;
    }

    try {
      await userService.deleteUser(userId);
      toast.success(t('users.userDeletedSuccess'));
      fetchUsers(); // Refresh the lists
    } catch (error) {
      let message = t('users.errorDeletingUser');
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      }
      toast.error(message);
    }
  };

  const changeUserRole = async (userId, username, currentRole, newRole) => {
    if (!window.confirm(t('users.confirmChangeRole', {
      username,
      currentRole: t(`users.${currentRole}`),
      newRole: t(`users.${newRole}`)
    }))) {
      return;
    }

    try {
      await userService.changeUserRole(userId, newRole);
      toast.success(t('users.roleChangedSuccess', { username }));
      fetchUsers(); // Refresh the lists
    } catch (error) {
      let message = t('users.errorChangingRole');
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      }
      toast.error(message);
    }
  };

  const resetUserPassword = async (userId, username) => {
    if (!window.confirm(t('users.confirmResetPassword', { username }))) {
      return;
    }

    try {
      const response = await userService.resetUserPassword(userId);
      const tempPassword = response.data?.temp_password || 'check-email';
      toast.success(t('users.passwordResetSuccess', { username, tempPassword }));
    } catch (error) {
      let message = t('users.errorResettingPassword');
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      }
      toast.error(message);
    }
  };

  const getRoleDisplayName = (role) => {
    return t(`users.${role}`);
  };

  // Amb 48 comptes, trobar-ne un a ull és baixar onze mil píxels.
  const usuarisVisibles = users.filter((u) => {
    const q = filtre.trim().toLowerCase();
    if (!q) return true;
    return (u.username || '').toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q);
  });

  if (loading) return <div className="loading">{t('users.loadingUsers')}</div>;


  return (
    <div>
      <h1>{t('users.userManagement')}</h1>

      {pendingUsers.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3>{t('users.pendingApprovals')} <span className="count-subtle">({pendingUsers.length})</span></h3>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>{t('users.username')}</th>
                  <th>{t('users.email')}</th>
                  <th>{t('users.role')}</th>
                  <th>{t('users.created')}</th>
                  <th>{t('users.action')}</th>
                </tr>
              </thead>
              <tbody>
                {pendingUsers.map(user => (
                  <tr key={user.id}>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td><span className="badge badge-warning">{getRoleDisplayName(user.role)}</span></td>
                    <td>{desDeUTC(user.created_at)?.toLocaleDateString() || ''}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                        <button
                          onClick={() => approveUser(user.id)}
                          className="btn btn-success"
                        >
                          {t('users.approve')}
                        </button>
                        <button
                          onClick={() => deleteUser(user.id, user.username)}
                          className="btn btn-danger"
                        >
                          {t('users.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: 'var(--spacing-xl)' }}>
        <div className="card-header capcalera-usuaris">
          <h3>
            {t('users.allUsers')}{' '}
            <span className="count-subtle">
              {filtre.trim()
                ? t('users.showing', { shown: usuarisVisibles.length, total: users.length })
                : `(${users.length})`}
            </span>
          </h3>
          <input
            type="search"
            className="filtre-usuaris"
            value={filtre}
            onChange={(e) => setFiltre(e.target.value)}
            placeholder={t('users.filterPlaceholder')}
            aria-label={t('users.filterPlaceholder')}
          />
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>{t('users.username')}</th>
                <th>{t('users.email')}</th>
                <th>{t('users.role')}</th>
                <th>{t('users.status')}</th>
                <th>{t('users.created')}</th>
                <th>{t('users.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {usuarisVisibles.map(user => (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`badge badge-${user.role === 'instructor' ? 'primary' : 'success'}`}>
                      {getRoleDisplayName(user.role)}
                    </span>
                  </td>
                  <td>
                    <span className={`badge badge-${user.is_approved ? 'success' : 'warning'}`}>
                      {user.is_approved ? t('users.approved') : t('users.pending')}
                    </span>
                  </td>
                  <td>{desDeUTC(user.created_at)?.toLocaleDateString() || ''}</td>
                  <td className="user-actions-column">
                    <div className="user-actions-container">
                      {/* Role Change Dropdown */}
                      <div className="user-role-selector">
                        <select
                          value={user.role}
                          onChange={(e) => {
                            if (e.target.value !== user.role) {
                              changeUserRole(user.id, user.username, user.role, e.target.value);
                            }
                          }}
                          className="user-role-dropdown"
                        >
                          <option value="analyst">{t('users.analyst')}</option>
                          <option value="instructor">{t('users.instructor')}</option>
                        </select>
                      </div>

                      {/* Action Buttons */}
                      <div className="user-action-buttons">
                        <button
                          onClick={() => resetUserPassword(user.id, user.username)}
                          className="user-action-btn user-action-btn-reset"
                          title={t('users.resetPassword')}
                        >
                          {t('users.resetPassword')}
                        </button>

                        {user.role !== 'instructor' && (
                          <button
                            onClick={() => deleteUser(user.id, user.username)}
                            className="user-action-btn user-action-btn-delete"
                            title={t('users.delete')}
                          >
                            {t('users.delete')}
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Users;